import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { notification } from "top-component-library";
import { generalErrorAlert } from "util/Utils";
import { SALESFORCE_ID_LENGTH, SALESFORCE_ID_PREFIX } from "./constants";
import {
  CustomerOption,
  CustomerPropertiesData,
  ExternalVendor,
  RestaurantChainConfig,
  SeatedCutSettings,
  SelectedCustomer,
  Station,
} from "./types";

const DEFAULT_RESTAURANT_CHAIN_CONFIG: RestaurantChainConfig = {
  source_customer: null,
  copy_billpayer: false,
  copy_stripe_account: false,
  copy_icons: false,
  link_employees: false,
};

// Mirrors customerPropertiesController.js. Fetches api/getOwnerProperties (shared with
// Account Settings, each page fetches its own copy) plus getAllSalesforceIds, which this
// page alone needs for the duplicate-ID check below.
export const useCustomerProperties = (selectedCustomer?: SelectedCustomer) => {
  const customerId = selectedCustomer?.customer_id;

  const [isLoading, setIsLoading] = useState(true);
  const [customer, setCustomer] = useState<CustomerPropertiesData | null>(null);
  const [salesforceIdsByCustomer, setSalesforceIdsByCustomer] = useState<Record<string, string>>({});
  const [seatedCut, setSeatedCut] = useState<SeatedCutSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [allowedCustomers, setAllowedCustomers] = useState<CustomerOption[]>([]);
  const [externalVendors, setExternalVendors] = useState<ExternalVendor[]>([]);
  const [vendorsChanged, setVendorsChanged] = useState(false);
  const [savingVendors, setSavingVendors] = useState(false);
  const [brandChildren, setBrandChildren] = useState<CustomerOption[]>([]);
  const [brandChildrenChanged, setBrandChildrenChanged] = useState(false);
  const [savingBrandChildren, setSavingBrandChildren] = useState(false);
  const [restaurantChainConfig, setRestaurantChainConfig] = useState<RestaurantChainConfig>(
    DEFAULT_RESTAURANT_CHAIN_CONFIG
  );
  const [importingChainConfig, setImportingChainConfig] = useState(false);
  const [possibleFulfillmentMethods, setPossibleFulfillmentMethods] = useState<Record<string, string>>({});
  const [savingStation, setSavingStation] = useState(false);
  const [selectedReferrer, setSelectedReferrer] = useState("bbot");
  const [deactivating, setDeactivating] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [deletingCustomer, setDeletingCustomer] = useState(false);
  const [switchingReferrer, setSwitchingReferrer] = useState(false);

  const loadSeatedCut = useCallback(async () => {
    if (!customerId) return;
    try {
      const res = await axios.get<{
        fraction_of_items_pretax: number;
        fraction_of_items_tax: number;
        fraction_of_servicecharge_pretax: number;
        fraction_of_servicecharge_tax: number;
      }>("/admin/getSeatedCut", { params: { customer_id: customerId } });
      setSeatedCut({
        seatedItemsPretax: 100 * res.data.fraction_of_items_pretax,
        seatedItemsTax: 100 * res.data.fraction_of_items_tax,
        seatedServiceChargePretax: 100 * res.data.fraction_of_servicecharge_pretax,
        seatedServiceChargeTax: 100 * res.data.fraction_of_servicecharge_tax,
        seatedMakeRetroactive: false,
      });
    } catch (error) {
      generalErrorAlert(error, "Trouble retrieving Seated Cut properties, please try again.");
    }
  }, [customerId]);

  const load = useCallback(async () => {
    if (!customerId) return;
    try {
      const [propertiesRes, salesforceIdsRes, allowedCustomersRes, vendorsRes, brandChildrenRes, fulfillmentRes] =
        await Promise.all([
          axios.get("api/getOwnerProperties", { params: { customer_id: customerId } }),
          axios.post<Record<string, string>>("/admin/getAllSalesforceIds"),
          axios.get<{ customers: CustomerOption[] }>("/owner/getAllowedCustomers"),
          axios.get<{ vendors: ExternalVendor[] }>("/admin/getExternalVendors"),
          axios.get<{ brand_children: CustomerOption[] }>("/admin/getBrandChildren"),
          axios.get<{ fulfillment_method_pretty_names: Record<string, string> }>(
            "/api/getFulfillmentMethodsAndStatusNames"
          ),
        ]);
      const properties = propertiesRes.data.owner_properties;
      setCustomer({
        ...properties,
        integrations: {
          ...properties.integrations,
          sevenrooms: properties.integrations?.sevenrooms ?? {},
          seated: properties.integrations?.seated ?? {},
        },
        internal_metadata: properties.internal_metadata ?? {},
      });
      setSalesforceIdsByCustomer(salesforceIdsRes.data);
      // Mirrors getAllowedCustomers(): the full list, unfiltered - each card that offers a
      // "pick another customer" dropdown (vendor add, brand child add) is responsible for
      // excluding itself and already-added entries, matching legacy's per-usage filtering
      // (isAllowedVendor/isAllowedBrandChild). The Restaurant Chain Config source picker
      // uses the unfiltered list too, exactly as legacy does.
      setAllowedCustomers(
        [...allowedCustomersRes.data.customers].sort((a, b) => a.name_for_admin.localeCompare(b.name_for_admin))
      );
      setExternalVendors(vendorsRes.data.vendors.map((vendor) => ({ ...vendor, isNew: false, addAllMenus: false })));
      setVendorsChanged(false);
      setBrandChildren(brandChildrenRes.data.brand_children);
      setBrandChildrenChanged(false);
      setPossibleFulfillmentMethods(fulfillmentRes.data.fulfillment_method_pretty_names);
      setSelectedReferrer(properties.referrer || "bbot");
      if (properties.referrer === "seated") await loadSeatedCut();
    } catch (error) {
      generalErrorAlert(error, "Error retrieving customer properties, please refresh.");
    }
  }, [customerId, loadSeatedCut]);

  useEffect(() => {
    const initialLoad = async () => {
      setIsLoading(true);
      await load();
      setIsLoading(false);
    };
    initialLoad();
  }, [load]);

  const setCustomerField = useCallback(
    <K extends keyof CustomerPropertiesData>(field: K, value: CustomerPropertiesData[K]) => {
      setCustomer((current) => (current ? { ...current, [field]: value } : current));
    },
    []
  );

  const setSevenroomsVenueId = useCallback((value: string) => {
    setCustomer((current) =>
      current
        ? { ...current, integrations: { ...current.integrations, sevenrooms: { sevenrooms_venue_id: value } } }
        : current
    );
  }, []);

  const setSeatedVenueId = useCallback((value: string) => {
    setCustomer((current) =>
      current ? { ...current, integrations: { ...current.integrations, seated: { seated_venue_id: value } } } : current
    );
  }, []);

  // Mirrors editOwnerProperties() (controller): validates, then posts the whole customer
  // object back. Takes the data to save explicitly, rather than reading the customer state
  // closure, so callers that both edit a field and save in the same action (unmuteCSAlerts,
  // the Fullstory auto-save) can't race their own setCustomer update. The backend requires
  // currency/locale/minimum_paid_order_cents/integrations on every call regardless of which
  // fields actually changed - see views_for_admins.editOwnerProperties, which reads them
  // unconditionally (no .get()) and 500s if they're missing. referrer is deliberately never
  // sent - see BasicInformationCard.
  //
  // Deliberately doesn't reload afterward: data already is the full customer object the
  // server just persisted (this endpoint round-trips everything), and refetching here would
  // stomp taxConfig/externalVendors/brandChildren with server data any time this fires,
  // discarding whatever the user had mid-edited on an unrelated card. Matches legacy, which
  // also never refetches after editOwnerProperties.
  const saveCustomer = useCallback(
    async (data: CustomerPropertiesData) => {
      if (!customerId) return false;

      let salesforceId = data.salesforce_id;
      if (salesforceId) {
        salesforceId = salesforceId.split(" ").join("");
        if (salesforceId in salesforceIdsByCustomer && salesforceIdsByCustomer[salesforceId] !== customerId) {
          notification.error({
            message:
              "Oops, another customer is using that Salesforce ID. Please re-enter the ID or use a different one.",
          });
          return false;
        }
        if (salesforceId.length !== SALESFORCE_ID_LENGTH || !salesforceId.startsWith(SALESFORCE_ID_PREFIX)) {
          notification.error({
            message: "The Salesforce ID is not the correct format. Please re-enter the ID or use a different one.",
          });
          return false;
        }
      }

      setSaving(true);
      try {
        await axios.post("/admin/editOwnerProperties", {
          customer_id: customerId,
          customer_name: data.customer_name,
          name_for_owner: data.name_for_owner,
          name_for_admin: data.name_for_admin,
          timezone: data.timezone,
          currency: data.currency,
          locale: data.locale,
          salesforce_id: salesforceId,
          is_dash_pay: data.is_dash_pay,
          dd_store_id: data.dd_store_id,
          integrations: data.integrations,
          minimum_paid_order_cents: data.minimum_paid_order_cents,
          allow_free_orders: data.allow_free_orders,
          allow_free_orders_after_purchase: data.allow_free_orders_after_purchase,
          internal_metadata: data.internal_metadata,
          closed_until: data.closed_until,
          default_kds_ticket_colors: data.default_kds_ticket_colors,
        });
        notification.success({ message: "Owner properties updated successfully!" });
        return true;
      } catch (error) {
        generalErrorAlert(error, "Trouble editing customer properties. Please refresh and try again.");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [customerId, salesforceIdsByCustomer]
  );

  const save = useCallback(
    () => (customer ? saveCustomer(customer) : Promise.resolve(false)),
    [customer, saveCustomer]
  );

  const saveSeatedCut = useCallback(async () => {
    if (!customerId || !seatedCut) return false;
    try {
      await axios.post("/admin/setSeatedCut", {
        customer_id: customerId,
        fraction_of_items_pretax: seatedCut.seatedItemsPretax / 100,
        fraction_of_items_tax: seatedCut.seatedItemsTax / 100,
        fraction_of_servicecharge_pretax: seatedCut.seatedServiceChargePretax / 100,
        fraction_of_servicecharge_tax: seatedCut.seatedServiceChargeTax / 100,
        make_retroactive: seatedCut.seatedMakeRetroactive,
      });
      notification.success({ message: "Seated Cut saved." });
      return true;
    } catch (error) {
      generalErrorAlert(error, "Trouble saving Seated Cut properties, please try again.");
      return false;
    }
  }, [customerId, seatedCut]);

  const setSeatedCutField = useCallback(<K extends keyof SeatedCutSettings>(field: K, value: SeatedCutSettings[K]) => {
    setSeatedCut((current) => (current ? { ...current, [field]: value } : current));
  }, []);

  const currentlyMuted = Boolean(customer?.closed_until && moment(customer.closed_until).isAfter(moment()));

  const muteCSAlerts = useCallback(() => {
    setCustomerField("closed_until", moment().add(48, "hours").toISOString());
  }, [setCustomerField]);

  // Mirrors unmuteCSAlertsAndSave(): sets closed_until into the past, then immediately
  // saves - the button has no separate "Save" step, unlike every other Customer Success
  // action. Builds the updated object explicitly rather than saving after setCustomer, since
  // that state update wouldn't be visible yet on this render's saveCustomer closure.
  const unmuteCSAlertsAndSave = useCallback(async () => {
    if (!customer) return false;
    const updated = { ...customer, closed_until: moment().add(-1, "hours").toISOString() };
    setCustomer(updated);
    return saveCustomer(updated);
  }, [customer, saveCustomer]);

  // Mirrors internal_metadata.fullstory's ng-change="editOwnerProperties()": this one
  // control alone auto-saves, unlike everything else on the page. Preserved as-is - Bbot
  // staff are used to it sticking immediately.
  const setFullstory = useCallback(
    async (checked: boolean) => {
      if (!customer) return false;
      const updated = { ...customer, internal_metadata: { ...customer.internal_metadata, fullstory: checked } };
      setCustomer(updated);
      return saveCustomer(updated);
    },
    [customer, saveCustomer]
  );

  const addVendor = useCallback((vendor: CustomerOption) => {
    setExternalVendors((current) => [
      ...current,
      {
        ...vendor,
        isNew: true,
        addAllMenus: false,
        vendee_sees_vendor_data: true,
        vendee_loyalty_api_controls_vendors: false,
      },
    ]);
    setVendorsChanged(true);
  }, []);

  const removeVendor = useCallback((vendorCustomerId: string) => {
    setExternalVendors((current) => current.filter((vendor) => vendor.customer_id !== vendorCustomerId));
    setVendorsChanged(true);
  }, []);

  const updateVendorField = useCallback(
    <K extends keyof ExternalVendor>(vendorCustomerId: string, field: K, value: ExternalVendor[K]) => {
      setExternalVendors((current) =>
        current.map((vendor) => (vendor.customer_id === vendorCustomerId ? { ...vendor, [field]: value } : vendor))
      );
      setVendorsChanged(true);
    },
    []
  );

  const saveExternalVendors = useCallback(async () => {
    if (!customerId) return false;
    setSavingVendors(true);
    try {
      await axios.post("/admin/setExternalVendors", { customer_id: customerId, vendors: externalVendors });
      notification.success({ message: "Vendors set." });
      setVendorsChanged(false);
      return true;
    } catch (error) {
      generalErrorAlert(error, "Trouble saving vendors. Please refresh and try again.");
      return false;
    } finally {
      setSavingVendors(false);
    }
  }, [customerId, externalVendors]);

  const addBrandChild = useCallback((child: CustomerOption) => {
    setBrandChildren((current) => [...current, child]);
    setBrandChildrenChanged(true);
  }, []);

  const removeBrandChild = useCallback((childCustomerId: string) => {
    setBrandChildren((current) => current.filter((child) => child.customer_id !== childCustomerId));
    setBrandChildrenChanged(true);
  }, []);

  const saveBrandChildren = useCallback(async () => {
    if (!customerId) return false;
    setSavingBrandChildren(true);
    try {
      await axios.post("/admin/setBrandChildren", {
        customer_id: customerId,
        brand_children: brandChildren.map((child) => child.customer_id),
      });
      notification.success({ message: "Brand children set." });
      setBrandChildrenChanged(false);
      return true;
    } catch (error) {
      generalErrorAlert(error, "Trouble saving brand children. Please refresh and try again.");
      return false;
    } finally {
      setSavingBrandChildren(false);
    }
  }, [customerId, brandChildren]);

  const setRestaurantChainConfigField = useCallback(
    <K extends keyof RestaurantChainConfig>(field: K, value: RestaurantChainConfig[K]) => {
      setRestaurantChainConfig((current) => ({ ...current, [field]: value }));
    },
    []
  );

  // Mirrors setPropertiesBasedOnAnotherCustomer(): posts the whole restaurantChainConfig
  // object as-is, with no customer_id - the backend resolves the destination customer from
  // the session, which WithCustomerInfo's awaited selectCustomer call keeps in sync before
  // this page's own data ever loads.
  const importFromAnotherCustomer = useCallback(async () => {
    setImportingChainConfig(true);
    try {
      await axios.post("/admin/setPropertiesBasedOnAnotherCustomer", restaurantChainConfig);
      notification.success({ message: "Imported." });
      setRestaurantChainConfig(DEFAULT_RESTAURANT_CHAIN_CONFIG);
      return true;
    } catch (error) {
      generalErrorAlert(error, "Trouble importing.");
      return false;
    } finally {
      setImportingChainConfig(false);
    }
  }, [restaurantChainConfig]);

  // addStation's response carries no station data (JsonResponse({})), so the new station's
  // server-assigned id can only be learned via a refetch - narrowed to stations alone (not the
  // full load()) so it doesn't stomp taxConfig/externalVendors/brandChildren with server data,
  // discarding whatever the user had mid-edited on an unrelated card.
  const addStation = useCallback(
    async (stationName: string, itemClass: string) => {
      if (!customerId) return false;
      setSavingStation(true);
      try {
        await axios.post("/admin/addStation", { customerId, stationName, itemClass });
        notification.success({ message: `Successfully added ${stationName}` });
        const res = await axios.get<{ owner_properties: { stations: Station[] } }>("api/getOwnerProperties", {
          params: { customer_id: customerId },
        });
        setCustomer((current) => (current ? { ...current, stations: res.data.owner_properties.stations } : current));
        return true;
      } catch (error) {
        generalErrorAlert(error, "Trouble adding station. Please refresh and try again.");
        return false;
      } finally {
        setSavingStation(false);
      }
    },
    [customerId]
  );

  // Mirrors confirmDeleteStation()'s last-station guard: checked here (rather than only in
  // the confirmation dialog) so it also covers a no-orders station, which StationsCard
  // deletes with no confirmation step at all.
  const deleteStation = useCallback(
    async (station: Station) => {
      if (!customerId || !customer) return false;
      if (customer.stations.length === 1) {
        notification.error({
          message:
            "You cannot delete the last station at a customer. Change the station's properties as needed, or contact engineering for help.",
        });
        return false;
      }
      setSavingStation(true);
      try {
        await axios.post("/admin/deleteStation", { stationId: station.id, customerId });
        notification.success({ message: `Successfully deleted ${station.station_name}` });
        setCustomer((current) =>
          current
            ? { ...current, stations: current.stations.filter((existing) => existing.id !== station.id) }
            : current
        );
        return true;
      } catch (error) {
        generalErrorAlert(error, "Trouble deleting station. Please refresh and try again.");
        return false;
      } finally {
        setSavingStation(false);
      }
    },
    [customerId, customer]
  );

  const addKdsColor = useCallback((fulfillmentMethod: string) => {
    setCustomer((current) =>
      current
        ? {
            ...current,
            default_kds_ticket_colors: { ...current.default_kds_ticket_colors, [fulfillmentMethod]: "#000000" },
          }
        : current
    );
  }, []);

  const removeKdsColor = useCallback((fulfillmentMethod: string) => {
    setCustomer((current) => {
      if (!current) return current;
      const nextColors = { ...current.default_kds_ticket_colors };
      delete nextColors[fulfillmentMethod];
      return { ...current, default_kds_ticket_colors: nextColors };
    });
  }, []);

  const setKdsColor = useCallback((fulfillmentMethod: string, hex: string) => {
    setCustomer((current) =>
      current
        ? { ...current, default_kds_ticket_colors: { ...current.default_kds_ticket_colors, [fulfillmentMethod]: hex } }
        : current
    );
  }, []);

  // Mirrors deactivateCustomer(): the typed name is NOT checked client-side (unlike station
  // deletion) - the server verifies it against customer.customer_name and rejects with a
  // VerificationFailedException if it doesn't match, surfaced like any other save error.
  const deactivateCustomer = useCallback(
    async (customerForVerification: string) => {
      if (!customerId) return false;
      setDeactivating(true);
      try {
        await axios.post("/admin/deactivateCustomer", { customer_id: customerId, customerForVerification });
        notification.success({ message: "Customer deactivated successfully. Redirecting to admin home..." });
        setTimeout(() => {
          window.location.href = "/admin/portal";
        }, 2500);
        return true;
      } catch (error) {
        generalErrorAlert(error, "Customer could not be deactivated. Please refresh and try again.");
        return false;
      } finally {
        setDeactivating(false);
      }
    },
    [customerId]
  );

  // Mirrors reactivateCustomer(): unlike deactivate/delete, this refetches instead of
  // redirecting - reactivating brings the customer back into active use of this same page.
  //
  // Deliberately kept as a full load(), unlike the other actions here that reload: customer_setup.reactivate_customer
  // doesn't just flip is_archived - it also strips the deactivation rename off customer_name/name_for_admin and
  // clears internal_metadata.can_be_reactivated, so a hand-picked field list would need to track that logic and
  // could silently drift from it. Reactivation is also a rare, deliberate admin action rather than something done
  // mid-edit on another card, so the risk load() is meant to avoid here is low.
  const reactivateCustomer = useCallback(async () => {
    if (!customerId) return false;
    setReactivating(true);
    try {
      const res = await axios.post<{ collisions?: boolean }>("/admin/reactivateCustomer", {
        customer_id: customerId,
      });
      notification.success({
        message: res.data.collisions
          ? "Reactivated customer. Some locations could not be fully reactivated because of collisions with other live locations. Go to Tables / Location Codes to update manually. Any vendor / vendee relationships were severed during deactivation and will need to be re-connected."
          : "Customer reactivated successfully. Any vendor / vendee relationships were severed during deactivation and will need to be re-connected.",
        duration: res.data.collisions ? 20 : 10,
      });
      await load();
      return true;
    } catch (error) {
      generalErrorAlert(error, "There was an error reactivating the customer. Please refresh and try again.");
      return false;
    } finally {
      setReactivating(false);
    }
  }, [customerId, load]);

  // Mirrors deleteCustomer(): legacy has zero confirmation before calling this - added a
  // confirm dialog here (DeleteCustomerDialog) since a hard, cascading delete with no
  // confirmation at all is worth the deviation, even though the button never actually
  // renders today: can_delete comes from canDeleteCustomer(), which is stubbed to always
  // return False, so customer_setup.deleteCustomer() always raises
  // CustomerHasOrdersException once thisIsProd() (and can_delete is False in every
  // environment as a result, not just prod).
  const deleteCustomerAccount = useCallback(async () => {
    if (!customerId) return false;
    setDeletingCustomer(true);
    try {
      await axios.post("/admin/deleteCustomer", { customer_id: customerId });
      notification.success({ message: "Customer deleted successfully. Redirecting to admin home..." });
      setTimeout(() => {
        window.location.href = "/admin/portal";
      }, 2500);
      return true;
    } catch (error) {
      generalErrorAlert(error, "Customer could not be deleted. Please refresh and try again.");
      return false;
    } finally {
      setDeletingCustomer(false);
    }
  }, [customerId]);

  // Mirrors switchPartnerAccess(): note customerId, not customer_id - the one endpoint on
  // this page that uses camelCase. Legacy never refetches after a successful switch,
  // leaving the (now stale) referrer shown in Basic Information unchanged until a manual
  // refresh; updating it locally here instead is a small, low-risk improvement over that gap.
  // Patches customer.referrer directly rather than calling load() - switch_referrer_and_set_default_admins_and_app_access
  // only ever touches referrer (plus an internal_metadata bookkeeping timestamp this page
  // never displays), and a full reload would also stomp taxConfig/externalVendors/brandChildren
  // with server data, discarding whatever the user had mid-edited on an unrelated card.
  // "bbot" is stored server-side as "" (see Customer.referrer), same as load() itself expects.
  const switchReferrer = useCallback(async () => {
    if (!customerId) return false;
    setSwitchingReferrer(true);
    try {
      await axios.post("/admin/switchCustomerPartnerAccess", { referrer: selectedReferrer, customerId });
      notification.success({ message: `Customer referrer has successfully changed to ${selectedReferrer}` });
      setCustomer((current) =>
        current ? { ...current, referrer: selectedReferrer === "bbot" ? "" : selectedReferrer } : current
      );
      if (selectedReferrer === "seated") await loadSeatedCut();
      return true;
    } catch (error) {
      generalErrorAlert(error, `Failed to change customer referrer to ${selectedReferrer}`);
      return false;
    } finally {
      setSwitchingReferrer(false);
    }
  }, [customerId, selectedReferrer, loadSeatedCut]);

  return {
    isLoading,
    saving,
    customer,
    setCustomerField,
    setSevenroomsVenueId,
    setSeatedVenueId,
    save,
    seatedCut,
    setSeatedCutField,
    saveSeatedCut,
    currentlyMuted,
    muteCSAlerts,
    unmuteCSAlertsAndSave,
    setFullstory,
    allowedCustomers,
    externalVendors,
    vendorsChanged,
    savingVendors,
    addVendor,
    removeVendor,
    updateVendorField,
    saveExternalVendors,
    brandChildren,
    brandChildrenChanged,
    savingBrandChildren,
    addBrandChild,
    removeBrandChild,
    saveBrandChildren,
    restaurantChainConfig,
    importingChainConfig,
    setRestaurantChainConfigField,
    importFromAnotherCustomer,
    savingStation,
    addStation,
    deleteStation,
    possibleFulfillmentMethods,
    addKdsColor,
    removeKdsColor,
    setKdsColor,
    selectedReferrer,
    setSelectedReferrer,
    deactivating,
    deactivateCustomer,
    reactivating,
    reactivateCustomer,
    deletingCustomer,
    deleteCustomerAccount,
    switchingReferrer,
    switchReferrer,
  };
};
